import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import * as XLSX from "xlsx";
import CircularProgress from "@mui/material/CircularProgress"
import { LoadNewOrganizations, LoadPhoneNumberGroups, LoadUsers } from '../../actions'
import { setBackHandler, setHeaderTitle } from '../../helpers/header.helper'
import { mapToSeatUsage, OrganizationModel, SeatUsage } from '../../models/organization.model'
import * as selector from '../../selectors/selectors'
import { AppRoutes } from '../routes/route.path'
import { pageTitles, ToasterTexts } from '../shared/constants'
import setToast from '../../helpers/toaster.helper'
import { ToasterType } from '../shared/toaster/toaster.model'
import { WhiteTickIcon } from '../../icon'
import SelectOrganizationStep from './select-organization-step.component'
import UploadFileStep from './upload-file-step.component'
import ValidationReportStep from './validation-report-step.component'
import CreateUsersResultStep from './create-users-result-step.component'
import { FILE_COLUMNS, ImportEditableRow, importRowsFromFile, normalizeHeader } from './import-users-shared.component'
import { UtilityService } from '../../services/UtilityService';
import { UserModel } from '../../models/users.model';
import { Role, RoleModel } from '../../models/roles.model';
import { OrganizationService } from '../../services/OrganizationService';
import { mapToPhoneNumber, PhoneNumbergroup, PhoneNumberModel } from '../../models/phone-number.model';
import { PhoneNumberService } from '../../services/PhonenumberService';
import { UserService } from '../../services/UserService';

const MAX_IMPORT_FILE_SIZE_BYTES = 25 * 1024 * 1024;

const getEditedCellValue = (row: ImportEditableRow, header: string): string | undefined => {
    const field = FILE_COLUMNS[normalizeHeader(header)] || header;
    if (field === "PhoneNumber" || field === "PrimaryPhoneNumber") {
        return row.PrimaryPhoneNumber || row.PhoneNumber || "";
    }
    if (field === "FirstName" || field === "LastName" || field === "Email" || field === "Role" || field === "SeatType" || field === "SecondaryPhoneNumbers" || field === "PhoneNumberGroups") {
        return row[field] || "";
    }
    return undefined;
};

const buildUpdatedUploadedFile = (file: File, originalRows: Record<string, string>[], editedRows: ImportEditableRow[]): File => {
    const headers = Object.keys(originalRows[0] || {});
    const data = editedRows.map((edited, index) => {
        const original = originalRows[edited.rowIndex] ?? originalRows[index] ?? {};
        const next: Record<string, string> = { ...original };
        (headers.length ? headers : Object.keys(next)).forEach((header) => {
            const value = getEditedCellValue(edited, header);
            if (value !== undefined) {
                next[header] = value;
            }
        });
        return next;
    });

    const worksheet = XLSX.utils.json_to_sheet(data, headers.length ? { header: headers } : undefined);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");

    const fileName = file.name.toLowerCase();
    if (fileName.endsWith(".csv")) {
        return new File([XLSX.utils.sheet_to_csv(worksheet)], file.name, { type: "text/csv" });
    }

    const bookType = fileName.endsWith(".xls") && !fileName.endsWith(".xlsx") ? "xls" : "xlsx";
    const buffer = XLSX.write(workbook, { bookType, type: "array" });
    return new File([buffer], file.name, {
        type: file.type || "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    });
};

const ImportUsersComponent = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const IsDarkMode: boolean = useSelector(selector.isDarkMode);
    const Organizations: OrganizationModel[] = useSelector(selector.newOrganizations);
    const PhoneNumberGroups: PhoneNumbergroup[] = useSelector(selector.phonenumbergroups);
    const users: UserModel[] = useSelector(selector.users);
    const [seats, setSeats] = useState<SeatUsage[]>([])
    const Roles: RoleModel[] = useSelector(selector.roles)
    const [PhoneNumbers, setPhoneNumbers] = useState<PhoneNumberModel[]>([]);

    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const stepContentScrollRef = useRef<HTMLDivElement>(null);

    const [activeStep, setActiveStep] = useState<number>(0);
    const [selectedOrgId, setSelectedOrgId] = useState<string>("");
    const [uploadedFile, setUploadedFile] = useState<File>();
    const [jsonFileData, setJsonFileData] = useState<Record<string, string>[]>([]);
    const [sheetCount, setSheetCount] = useState<number>(0);
    const [fileUploadError, setFileUploadError] = useState<string>("");
    const [importRows, setImportRows] = useState<ImportEditableRow[]>([]);
    const [errorRows, setErrorRows] = useState<ImportEditableRow[]>([]);
    const [isCreating, setIsCreating] = useState<boolean>(false);
    const [createCount, setCreateCount] = useState<number>(0);
    const [importSubmitted, setImportSubmitted] = useState<boolean>(false);

    const Steps = [
        { label: "Select organization", description: "Applies to every row" },
        { label: "Upload file", description: "Falkon Excel template" },
        { label: "Validation report", description: "Failed rows and reasons" },
        { label: "Create users", description: "Runs in the background" }
    ];

    const getPhoneNumbers = (id) => {
        PhoneNumberService.getPhoneNumbersByOrgId(id).then((res) => {
            if (res.status === 200) {
                setPhoneNumbers(res.data.map(mapToPhoneNumber));
            }
        }).catch((err) => {
        })
    }

    const getSeatDetails = (id: string) => {
        OrganizationService.getSeatDetails(id)
            .then(res => {
                let seats = res.data.seatUsage.map(mapToSeatUsage)
                setSeats(seats?.filter(item => !item.SeatType?.toLowerCase().includes("10dlc") && item.Users.Total > item.Users.Used))
            }).catch(err => {

            });
    }

    const resetFileInput = () => {
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const handleClearFile = () => {
        setUploadedFile(undefined);
        setJsonFileData([]);
        setSheetCount(0);
        setFileUploadError("");
        setImportRows([]);
        resetFileInput();
    };

    const getRowErrors = useCallback((rows: ImportEditableRow[]): ImportEditableRow[] => {
        const remainingBySeatType = new Map(
            seats.map((seat) => [seat.SeatType?.toLowerCase(), seat.Users.Total - seat.Users.Used])
        )

        return rows.map((row) => {
            const seatTypeKey = row.SeatType?.toLowerCase()?.trim()
            const remaining = remainingBySeatType.get(seatTypeKey)
            const primaryPhoneNumber = UtilityService.unmaskPhoneNumberWithPlus(row.PrimaryPhoneNumber || (row as any).PhoneNumber)
            const roleId = Roles.find(x => x.Name?.toLowerCase() === row.Role?.toLowerCase())?.ID
            const selectedGroups = (row.PhoneNumberGroups || "").split(";").map(x => x.trim()).filter(Boolean)
            const secondaryPhoneNumbers = (row.SecondaryPhoneNumbers || "").split(";").map(x => UtilityService.unmaskPhoneNumberWithPlus(x.trim())).filter(Boolean)
            const groupsForPrimary = roleId === Role.MANAGER && selectedGroups.length
                ? PhoneNumberGroups.filter(group => selectedGroups.some(name => name.toLowerCase() === group.GroupName?.toLowerCase()))
                : PhoneNumberGroups
            const groupPhoneNumberIds: string[] = groupsForPrimary.flatMap((x: any) => (x.PhoneNumbers || []).map((x: any) => x.phoneNumberId)) || []
            const filteredPhoneNumbers: PhoneNumberModel[] = PhoneNumbers?.filter(pn => groupPhoneNumberIds.includes(pn.ID));

            const allowedSecondary = PhoneNumbers.filter((phone) => {
                const phoneNumber = UtilityService.unmaskPhoneNumberWithPlus(phone.PhoneNumber) || phone.PhoneNumber
                const sameOrg = !phone.OrganizationID || !selectedOrgId || phone.OrganizationID === selectedOrgId
                return sameOrg && !phone.IsDeleted && phoneNumber !== primaryPhoneNumber
            })

            let groupsError = ""
            if (roleId === Role.MANAGER) {
                if (!selectedGroups.length) {
                    groupsError = "Line Group is required"
                } else if (selectedGroups.some(name => !PhoneNumberGroups.some(group => group.GroupName?.toLowerCase() === name.toLowerCase()))) {
                    groupsError = "Line Group is invalid"
                }
            } else if (selectedGroups.length) {
                groupsError = "Line Group is invalid"
            }

            let secondaryError = ""
            if (roleId === Role.USER) {
                if (secondaryPhoneNumbers.some(number => !allowedSecondary.some(phone => (UtilityService.unmaskPhoneNumberWithPlus(phone.PhoneNumber) || phone.PhoneNumber) === number))) {
                    secondaryError = "Secondary Phone Number is invalid"
                }
            } else if (secondaryPhoneNumbers.length) {
                secondaryError = "Secondary Phone Number is invalid"
            }

            let seatTypeError = ""
            if (row.SeatType?.trim().length === 0) {
                seatTypeError = "Seat Type is required"
            } else if (remaining === undefined) {
                seatTypeError = "Seat Type is invalid"
            } else if (remaining <= 0) {
                seatTypeError = "No Seats available for this seat type"
            } else {
                remainingBySeatType.set(seatTypeKey, remaining - 1)
            }

            if (row.Role) {
                row.Role = row.Role.toLowerCase();
                if (row.Role === "OrgAdmin" || row.Role === "Admin") {
                    row.Role = "organization admin";
                }
            }

            return {
                RowId: row.RowId,
                rowIndex: row.rowIndex,
                FirstName: row.FirstName.length === 0 ? "First Name is required" : "",
                LastName: row.LastName.length === 0 ? "Last Name is required" : "",
                Email: row.Email.length === 0 ? "Email is required" : UtilityService.isValidEmail(row.Email) ? users.some(user => user.Email === row.Email) ? "Email already exists" : "" : "Invalid email address",
                Role: row.Role.length === 0 ? "Role is required" : Roles.find(x => x.Name?.toLowerCase() === row.Role?.toLowerCase()) ? "" : "Role is invalid",
                SeatType: seatTypeError,
                PhoneNumber: primaryPhoneNumber.length === 0 ? "Primary Phone Number is required" : row.Role.toLowerCase() === "manager" ? filteredPhoneNumbers.some(x => x.PhoneNumber === primaryPhoneNumber) ? "" : "Primary Phone Number is invalid" : PhoneNumbers.some(x => x.PhoneNumber === primaryPhoneNumber) ? "" : "Primary Phone Number is invalid",
                PrimaryPhoneNumber: primaryPhoneNumber.length === 0 ? "Primary Phone Number is required" : row.Role.toLowerCase() === "manager" ? filteredPhoneNumbers.some(x => x.PhoneNumber === primaryPhoneNumber) ? "" : "Primary Phone Number is invalid" : PhoneNumbers.some(x => x.PhoneNumber === primaryPhoneNumber) ? "" : "Primary Phone Number is invalid",
                SecondaryPhoneNumbers: secondaryError,
                PhoneNumberGroups: groupsError,
            };
        })
    }, [seats, PhoneNumberGroups, PhoneNumbers, users, Roles, selectedOrgId])

    const handleValidateRows = () => {
        if (!uploadedFile || !selectedOrgId) return;
        const rows = importRowsFromFile(jsonFileData);
        setImportRows(rows);
        setErrorRows(getRowErrors(rows));
        setActiveStep(2);
    };

    const handleRowsChange = useCallback((rows: ImportEditableRow[]) => {
        setImportRows(rows);
        setErrorRows(getRowErrors(rows));
    }, [getRowErrors]);

    const handleCreateUsers = (summary: { created: number; skipped: number; failed: number }) => {
        if (!uploadedFile || isCreating) return;

        const formData = new FormData();
        const updatedFile = buildUpdatedUploadedFile(uploadedFile, jsonFileData, importRows);
        formData.append("file", updatedFile);

        setCreateCount(summary.created);
        setIsCreating(true);
        UserService.bulkCreate(formData, selectedOrgId)
            .then((res) => {
                if (res.status === 200) {
                    setImportSubmitted(true);
                    setActiveStep(3);
                } else {
                    setToast(ToasterTexts.SOMETHING_WENT_WRONG_ERROR, ToasterType.DANGER);
                }
            })
            .catch((err) => {
                setToast(err?.response?.data?.message || ToasterTexts.SOMETHING_WENT_WRONG_ERROR, ToasterType.DANGER);
            })
            .finally(() => {
                setIsCreating(false);
            });
    };

    const handleUploadAnotherFile = () => {
        handleClearFile();
        setErrorRows([]);
        setImportSubmitted(false);
        setCreateCount(0);
        setActiveStep(1);
    };

    const handleFileUpload = (file: File | undefined) => {
        if (!file) return;

        if (file.size > MAX_IMPORT_FILE_SIZE_BYTES) {
            setFileUploadError("File size should not exceed 25 MB");
            return;
        }

        setFileUploadError("");

        const reader = new FileReader();
        const isCSV = file.name.toLowerCase().endsWith(".csv");
        if (isCSV) {
            reader.readAsText(file);
        } else {
            reader.readAsArrayBuffer(file);
        }

        reader.onload = (event: any) => {
            let workbook;

            if (isCSV) {
                workbook = XLSX.read(event.target.result, { type: "string", cellText: true });
            } else {
                const data = new Uint8Array(event.target.result);
                workbook = XLSX.read(data, { type: "array", cellText: true });
            }

            const sheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[sheetName];
            const jsonData = XLSX.utils.sheet_to_json<Record<string, string>>(worksheet, {
                defval: "",
                raw: false
            });

            if (!jsonData || jsonData.length === 0) {
                setFileUploadError("file contains no data.");
                return;
            }

            if (jsonData.length >= 20000) {
                setFileUploadError("file contains more then 20000 rows");
                return;
            }

            const hasValidRow = jsonData.some((row) =>
                Object.values(row).some((cell) => cell?.toString().trim() !== "")
            );

            if (!hasValidRow) {
                setFileUploadError("At least one row with data is required.");
                return;
            }

            setUploadedFile(file);
            setJsonFileData(jsonData);
            setSheetCount(workbook.SheetNames?.length || 1);
        };
    };

    const renderStepContent = (step: number) => {
        switch (step) {
            case 0:
                return (
                    <SelectOrganizationStep
                        Organizations={Organizations}
                        SelectedOrgId={selectedOrgId}
                        IsDarkMode={IsDarkMode}
                        OnChangeOrg={setSelectedOrgId}
                        OnCancel={() => navigate(AppRoutes.NEW_USERS)}
                        OnContinue={() => {
                            if (selectedOrgId) {
                                setActiveStep(1);
                            }
                        }}
                    />
                );
            case 1:
                return (
                    <UploadFileStep
                        IsDarkMode={IsDarkMode}
                        UploadedFile={uploadedFile}
                        RowCount={jsonFileData.length}
                        SheetCount={sheetCount}
                        FileUploadError={fileUploadError}
                        FileInputRef={fileInputRef}
                        OnFileUpload={handleFileUpload}
                        OnClearFile={handleClearFile}
                        OnBack={() => setActiveStep(0)}
                        OnValidate={handleValidateRows}
                    />
                );
            case 2:
                return (
                    <ValidationReportStep
                        IsDarkMode={IsDarkMode}
                        RowCount={jsonFileData.length}
                        Rows={importRows}
                        ErrorRows={errorRows}
                        OnRowsChange={handleRowsChange}
                        OnBack={() => setActiveStep(1)}
                        OnCreate={handleCreateUsers}
                        Seats={seats}
                        Roles={Roles}
                        PhoneNumbers={PhoneNumbers}
                        PhoneNumberGroups={PhoneNumberGroups}
                        SelectedOrgId={selectedOrgId}
                        GetRowErrors={getRowErrors}
                    />
                );
            case 3:
                return importSubmitted ? (
                    <CreateUsersResultStep
                        IsDarkMode={IsDarkMode}
                        FileName={uploadedFile?.name}
                        OnViewReports={() => navigate(AppRoutes.NEW_USERS_IMPORT_REPORTS, { state: { OrganizationID: selectedOrgId } })}
                        OnUploadAnother={handleUploadAnotherFile}
                    />
                ) : null;
            default:
                return null;
        }
    };

    useEffect(() => {
        setHeaderTitle(pageTitles.BULK_ADD_USERS);
        setBackHandler(() => navigate(AppRoutes.NEW_USERS));
        if (!Organizations?.length) {
            LoadNewOrganizations(dispatch);
        }
        if (!users?.length) {
            LoadUsers(dispatch);
        }

        return () => {
            setBackHandler();
        };
    }, []);

    useEffect(() => {
        const scrollContainer = stepContentScrollRef.current;
        if (!scrollContainer) return;

        scrollContainer.scrollTop = 0;
        requestAnimationFrame(() => {
            scrollContainer.scrollTop = 0;
        });
    }, [activeStep]);

    useEffect(() => {
        if (UserService.hasSysAdminRole() && selectedOrgId) {
            getSeatDetails(selectedOrgId);
            getPhoneNumbers(selectedOrgId);
            LoadPhoneNumberGroups(dispatch, selectedOrgId);
        }
    }, [selectedOrgId]);

    return (
        <div ref={stepContentScrollRef} className={`w-p-98 mx-auto h-p-98 o-y-auto ${IsDarkMode ? "bg-color-primary-background" : "bg-color-report-background"} border-primary-divider`}>
            <div className='w-p-95 mx-auto pb-2'>
                <div className='d-flex align-center w-p-100'>
                    <div className={`w-p-100 mx-auto br-5 py-2 bg-color-secondary border-primary-divider`}>
                        <div className='d-flex align-center w-p-95 mx-auto py-1'>
                            {Steps.map((step, index) => {
                                const currentStep = isCreating ? 3 : activeStep;
                                const isCompleted = index < currentStep;
                                const isHighlighted = index <= currentStep;
                                return (
                                    <React.Fragment key={step.label}>
                                        <div className='d-flex align-center'>
                                            <div
                                                className={`d-flex align-center justify-center h4-medium br-50 ${isHighlighted ? "bg-color-primary-btn color-secondary" : "color-text-field-label"}`}
                                                style={{
                                                    width: 32,
                                                    height: 32,
                                                    minWidth: 32,
                                                    backgroundColor: isHighlighted ? undefined : "#EEF2F4"
                                                }}
                                            >
                                                {isCompleted ? <WhiteTickIcon /> : index + 1}
                                            </div>
                                            <div className='d-flex flex-direction ml-1'>
                                                <div className={`h4-medium w-space ${isHighlighted ? "color-primary-accent" : "color-text-field-label"}`}>
                                                    {step.label}
                                                </div>
                                                <div className='h5-regular color-menu-list-font-subnav w-space'>
                                                    {step.description}
                                                </div>
                                            </div>
                                        </div>
                                        {index < Steps.length - 1 && (
                                            <div className='flex-1 mx-2' style={{ height: 1, backgroundColor: "#E6E6E6" }} />
                                        )}
                                    </React.Fragment>
                                );
                            })}
                        </div>
                    </div>
                </div>
                {isCreating ? (
                    <div className={`w-p-100 mt-2 d-flex flex-direction align-center justify-center ${IsDarkMode ? "bg-color-primary-background" : "bg-color-secondary"}`} style={{ minHeight: 420 }}>
                        <CircularProgress size={40} thickness={4} sx={{ color: "#1450FF" }} />
                        <div className="h3-medium color-primary-accent mt-2" style={{ fontFamily: "silkasemibold" }}>
                            Creating {createCount} {createCount === 1 ? "user" : "users"}
                        </div>
                    </div>
                ) : (
                    <div className='d-flex mx-auto flex-direction'>
                        {renderStepContent(activeStep)}
                    </div>
                )}
            </div>
        </div>
    );
};

export default ImportUsersComponent;
